import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { GRID_TREE_DATA_GROUPING_FIELD, GridColDef, GridRenderCellParams, useGridApiRef } from '@mui/x-data-grid-pro';
import { Trans } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { FulfillmentPlan, GetFulfillmentPlanCounts, Step, StepType } from '../data/FulfillmentPlanData';
import { StepCounts } from '../data/SampleTrackingData';
import { LoadingProps, LoadState } from '../components/LoadingStateUtil';
import { CompactGridWrapper } from '../components/grid/CompactGridWrapper';
import useAuth from '../auth/UseAuth';
import { Typography, Divider, Box } from '@mui/material';
import { FlexBox } from '../components/FlexBox';
import { LightTooltip } from '../components/grid/cell/GridCellTooltip';
import useMemoTranslation from '../hooks/UseMemoTranslation';
import { TransitionTableHeaderIcon } from '../util/TransitionUtil';
import { TransitionTypeExtended } from 'researchProjectManagement/sampleTracking/SampleTrackingGrid';
import { GridCellCallBack } from '../components/grid/GridCellCallback';
import { FulfillmentPlanTransitionModal } from './FulfillmentPlanTransitionModal';
import { DialogOpenButton } from '../components/DialogOpenButton';
import FileDownloadIcon from '@mui/icons-material/FileDownload';

interface FulfillmentPlansGridProps {
  loadingProps: LoadingProps;
}

interface ModalState {
  stepTypeId: string;
  fulfillmentPlanId: string;
  researchProjectId?: string;
}

type Row = {
  id: string;
  name: string;
  description?: string;
  hierarchy: string[];
  stepTypes?: Record<string, StepType>;
  isResearchProject?: boolean;
  fulfillmentPlanId?: string;
  researchProjectId?: string;
};

const exportFlattenedData = (fulfillmentPlans: FulfillmentPlan[], t: any) => {
  const countLabels = {
    workLeftCount: t('needsCheckBy'),
    passedCount: t('passed'),
    failedCount: t('failed'),
    remainingCount: t('remaining'),
    completedCount: t('exited'),
    totalCount: t('total'),
  };

  const rows = fulfillmentPlans
    .flatMap(plan => [
      { name: plan.name, project: '', stepTypes: plan.stepTypes },
      ...(plan.researchProjects?.map(p => ({ name: plan.name, project: p.name, stepTypes: p.stepTypes })) || []),
    ])
    .map(({ name, project, stepTypes }) => {
      const row: any = { 'Fulfillment Plan Name': name, 'Research Project Name': project };

      Object.values(stepTypes || {}).forEach(stepType => {
        const displayName = getDisplayName(stepType.displayName);
        const counts = stepType.all || {};
        Object.entries(countLabels).forEach(
          ([field, label]) => (row[`${displayName} - ${label}`] = (counts as any)[field] || 0)
        );
      });

      return row;
    });

  const headers = Object.keys(rows[0] || {});
  const csv = [headers, ...rows.map(r => headers.map(h => `"${r[h] || ''}"`))].map(row => row.join(',')).join('\n');

  const link = document.createElement('a');
  link.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
  link.download = `fulfillment-plans-export-${new Date().toISOString().split('T')[0]}.csv`;
  link.click();
};

const DEFAULT_COLUMN_ORDER: string[] = [
  'Subsetting',
  'Shipping',
  'Receiving',
  'Extraction',
  'Quantification',
  'GenericWithSampleOutput',
  'LibraryPrep',
  'Sequencing',
  'Informatics',
  'ReadyForDelivery',
  'Delivered',
  'ReBiobanking',
  'Generic',
];

const displayNameToStepTypeId = (displayName: string): string => {
  if (displayName === 'Other') return '__other__';
  return displayName.charAt(0).toLowerCase() + displayName.slice(1);
};

const shouldExcludeFromRegularColumn = (stepName: string): boolean => {
  return stepName === 'Failed ILS Acceptance Criteria';
};

const getDisplayName = (originalDisplayName: string): string => {
  return originalDisplayName === 'GenericWithSampleOutput' ? 'Assign ID' : originalDisplayName;
};

// Process step types to separate excluded steps into "Other" and sort by DEFAULT_COLUMN_ORDER
// Temporary hardcoded logic till fulfillment plan features implemented
const processStepTypes = (stepTypes?: Record<string, StepType>): Record<string, StepType> | undefined => {
  if (!stepTypes) return stepTypes;

  const stepTypeEntries: Array<[string, StepType]> = [];
  const otherSteps: Step[] = [];
  const createEmptyCounts = () => ({
    workLeftCount: 0,
    passedCount: 0,
    failedCount: 0,
    remainingCount: 0,
    completedCount: 0,
    totalCount: 0,
    passedCompletedCount: 0,
    failedCompletedCount: 0,
  });
  const otherCounts: StepCounts = createEmptyCounts();

  const addStepToCounts = (step: Step, counts: StepCounts) => {
    (Object.keys(counts) as Array<keyof StepCounts>).forEach(key => {
      (counts[key] as number) += step[key] || 0;
    });
  };

  Object.entries(stepTypes).forEach(([stepTypeId, stepType]) => {
    const includedSteps = stepType.steps?.filter(step => !shouldExcludeFromRegularColumn(step.name)) || [];
    const excludedSteps = stepType.steps?.filter(step => shouldExcludeFromRegularColumn(step.name)) || [];

    const recalculatedAll: StepCounts = createEmptyCounts();
    includedSteps.forEach(step => addStepToCounts(step, recalculatedAll));
    excludedSteps.forEach(step => addStepToCounts(step, otherCounts));

    stepTypeEntries.push([
      stepTypeId,
      {
        ...stepType,
        steps: includedSteps,
        all: recalculatedAll,
      },
    ]);

    otherSteps.push(...excludedSteps);
  });

  stepTypeEntries.sort((a, b) => {
    const indexA = DEFAULT_COLUMN_ORDER.indexOf(a[1].displayName);
    const indexB = DEFAULT_COLUMN_ORDER.indexOf(b[1].displayName);
    return indexA - indexB;
  });

  const processed: Record<string, StepType> = {};
  stepTypeEntries.forEach(([stepTypeId, stepType]) => {
    processed[stepTypeId] = stepType;
  });

  if (otherSteps.length > 0 || otherCounts.totalCount > 0) {
    processed['__other__'] = {
      displayName: 'Other',
      all: otherCounts,
      steps: otherSteps,
    };
  }

  return processed;
};

export const FulfillmentPlansGrid = ({ loadingProps }: FulfillmentPlansGridProps) => {
  const { accessToken } = useAuth();
  const { t } = useMemoTranslation();
  const history = useHistory();
  const [loadingState, setLoadingState] = loadingProps;
  const apiRef = useGridApiRef();
  const [fulfillmentPlans, setFulfillmentPlans] = useState<FulfillmentPlan[]>([]);
  const [selectedTransition, setSelectedTransition] = useState<ModalState | null>(null);

  const loadFulfillmentPlans = useCallback(async () => {
    if (!accessToken) {
      return;
    }

    const plans = await GetFulfillmentPlanCounts(accessToken);

    const processedPlans: FulfillmentPlan[] = plans.map(plan => ({
      ...plan,
      stepTypes: processStepTypes(plan.stepTypes),
      researchProjects: plan.researchProjects?.map(project => ({
        ...project,
        stepTypes: processStepTypes(project.stepTypes) || {},
      })),
    }));

    setFulfillmentPlans(processedPlans);
  }, [accessToken]);

  useEffect(() => {
    return LoadState(setLoadingState, async () => {
      await loadFulfillmentPlans();
    });
  }, [loadFulfillmentPlans, setLoadingState]);

  const columns = useColumns(setSelectedTransition, fulfillmentPlans);
  const rows = useRows(fulfillmentPlans);

  const handleCellClick = useCallback(
    (params: any) => {
      if (params.field === '__tree_data_group__' && params.row.isResearchProject && params.row.researchProjectId) {
        history.push(`/research-project/${params.row.researchProjectId}`);
      }
    },
    [history]
  );

  const handleCustomExport = useCallback(() => {
    exportFlattenedData(fulfillmentPlans, t);
  }, [fulfillmentPlans, t]);

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
        <DialogOpenButton title={t('export')} onClick={handleCustomExport}>
          <FileDownloadIcon />
        </DialogOpenButton>
      </Box>
      <CompactGridWrapper
        apiRef={apiRef}
        rows={rows}
        columns={columns}
        loadingState={loadingState}
        getRowId={row => row.id}
        disableColumnFilter={true}
        disableColumnMenu={true}
        hideFooterSelectedRowCount={true}
        getTreeDataPath={row => row.hierarchy}
        treeData
        groupingColDef={{
          headerAlign: 'left',
          align: 'left',
          headerName: t('fulfillmentPlans'),
          width: 300,
          cellClassName: params => {
            return params.row.isResearchProject ? 'clickable-research-project' : '';
          },
        }}
        disableRowSelectionOnClick
        onCellClick={handleCellClick}
        getRowHeight={() => 'auto'}
        columnHeaderHeight={75}
        initialState={{
          pinnedColumns: { left: [GRID_TREE_DATA_GROUPING_FIELD] },
          sorting: {
            sortModel: [{ field: 'name', sort: 'asc' }],
          },
        }}
        sx={{
          '& .clickable-research-project': {
            color: 'primary.main',
            cursor: 'pointer',
            '&:hover': {
              color: 'primary.dark',
            },
          },
        }}
      />
      {selectedTransition && (
        <FulfillmentPlanTransitionModal
          open
          onClose={() => setSelectedTransition(null)}
          stepTypeId={selectedTransition.stepTypeId}
          fulfillmentPlanId={selectedTransition.fulfillmentPlanId}
          researchProjectId={selectedTransition.researchProjectId}
          fulfillmentPlans={fulfillmentPlans}
        />
      )}
      <Typography variant={'subtitle1'} mt={2}>
        <Trans t={t} i18nKey='countByLegend' />
      </Typography>
    </>
  );
};

const useRows = (fulfillmentPlans: FulfillmentPlan[]) => {
  return useMemo(() => {
    const rows: Row[] = [];

    fulfillmentPlans.forEach(plan => {
      rows.push({
        id: plan.fulfillmentPlanId,
        name: plan.name,
        description: plan.description,
        hierarchy: [plan.name],
        stepTypes: plan.stepTypes,
        fulfillmentPlanId: plan.fulfillmentPlanId,
      });

      if (plan.researchProjects) {
        plan.researchProjects.forEach(project => {
          rows.push({
            id: `${plan.fulfillmentPlanId}_${project.researchProjectId}`,
            name: project.name,
            hierarchy: [plan.name, project.name],
            stepTypes: project.stepTypes,
            isResearchProject: true,
            fulfillmentPlanId: plan.fulfillmentPlanId,
            researchProjectId: project.researchProjectId,
          });
        });
      }
    });

    return rows;
  }, [fulfillmentPlans]);
};

const EmptyCountsDisplay = () => (
  <FlexBox flexDirection={'column'} paddingTop={1} paddingBottom={1}>
    {['N:', 'P:', 'F:', 'R:', 'E:', 'T:'].map((label, i) => (
      <React.Fragment key={i}>
        {label === 'E:' && <Divider sx={{ bgcolor: 'black' }} />}
        <FlexBox sx={{ gap: 0.5, alignItems: 'center' }}>
          <Typography variant={'h6'} sx={{ fontSize: '12px', fontWeight: 'bold', width: '30%' }}>
            {label}
          </Typography>
          <Typography variant='body2' sx={{ fontWeight: 'bold', color: 'primary.main' }}>
            --
          </Typography>
        </FlexBox>
      </React.Fragment>
    ))}
  </FlexBox>
);

function humanize(str: string) {
  return str
    .replace(/^[\s_]+|[\s_]+$/g, '')
    .replace(/[_\s]+/g, ' ')
    .replace(/^[a-z]/, function (m) {
      return m.toUpperCase();
    });
}

const useColumns = (
  setSelectedTransition: React.Dispatch<React.SetStateAction<ModalState | null>>,
  fulfillmentPlans: FulfillmentPlan[]
): GridColDef[] => {
  const { t } = useMemoTranslation();

  const gridCellRenderer = useCallback(
    (stepTypeId: string) => {
      const CellRenderer = (params: GridRenderCellParams) => {
        const stepType = params.row?.stepTypes?.[stepTypeId];
        const counts = stepType?.all;

        if (!counts) {
          return <EmptyCountsDisplay />;
        }

        const items = [
          {
            label: 'N:',
            fullLabel: t('needsCheckBy'),
            count: `${counts.workLeftCount}`,
          },
          {
            label: 'P:',
            fullLabel: t('passed'),
            count: `${counts.passedCount}`,
          },
          {
            label: 'F:',
            fullLabel: t('failed'),
            count: `${counts.failedCount}`,
          },
          {
            label: 'R:',
            fullLabel: t('remaining'),
            count: `${counts.remainingCount}`,
          },
          {
            label: 'E:',
            fullLabel: t('exited'),
            count: `${counts.completedCount}`,
          },
          {
            label: 'T:',
            fullLabel: t('total'),
            count: `${counts.totalCount}`,
          },
        ];

        return (
          <FlexBox flexDirection={'column'} paddingTop={1} paddingBottom={1}>
            {items.map((item, i) => (
              <React.Fragment key={i}>
                {item.label === 'E:' && <Divider sx={{ bgcolor: 'black' }} />}
                <FlexBox sx={{ gap: 0.5, alignItems: 'center' }}>
                  <Typography variant={'h6'} sx={{ fontSize: '12px', fontWeight: 'bold', width: '30%' }}>
                    <LightTooltip title={item.fullLabel}>
                      <span>{item.label}</span>
                    </LightTooltip>
                  </Typography>
                  <GridCellCallBack
                    text={item.count === '0' ? '--' : item.count}
                    textStyle={{ fontWeight: 'bold' }}
                    callBack={() => {
                      const fulfillmentPlanId = params.row?.fulfillmentPlanId;
                      if (fulfillmentPlanId) {
                        setSelectedTransition({
                          stepTypeId,
                          fulfillmentPlanId,
                          researchProjectId: params.row?.researchProjectId,
                        });
                      }
                    }}
                    sx={{ padding: 0 }}
                  />
                </FlexBox>
              </React.Fragment>
            ))}
          </FlexBox>
        );
      };

      CellRenderer.displayName = `StepTypeCellRenderer_${stepTypeId}`;
      return CellRenderer;
    },
    [t, setSelectedTransition]
  );

  return useMemo(() => {
    const defaultColumns = [...DEFAULT_COLUMN_ORDER, 'Other'];

    return defaultColumns.map(displayName => {
      const stepTypeId = displayNameToStepTypeId(displayName);
      const finalDisplayName = getDisplayName(displayName);

      return {
        field: stepTypeId,
        headerName: finalDisplayName,
        renderHeader: () => (
          <TransitionTableHeaderIcon
            transitionType={displayName as TransitionTypeExtended}
            header={humanize(finalDisplayName)}
          />
        ),
        headerAlign: 'center',
        align: 'center',
        minWidth: 150,
        maxWidth: 300,
        flex: 1,
        renderCell: gridCellRenderer(stepTypeId),
      } as GridColDef;
    });
  }, [gridCellRenderer]);
};
